/**
 * Documentation Layout
 *
 * Three-column layout with:
 * - Left sidebar for navigation
 * - Main content area
 * - Right sidebar for table of contents
 *
 * WCAG 2.1 AA compliant with:
 * - Skip link for keyboard navigation
 * - Proper heading hierarchy
 * - Focus management
 * - Reduced motion support
 */

import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import DocsSidebar from './DocsSidebar';
import DocsSearch from './DocsSearch';
import MDXProvider from '../../components/docs/MDXProvider';
import Logo from '../../components/brand/Logo';

export default function DocsLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close mobile sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack"
      >
        Skip to main content
      </a>

      {/* Top navigation */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo and main nav */}
            <div className="flex items-center gap-8">
              <Link to="/" aria-label="ProgramRow home" className="inline-flex">
                <Logo size={14} on="light" wordmarkSize={19} />
              </Link>

              <nav className="hidden md:flex items-center gap-6" aria-label="Main">
                <Link
                  to="/docs"
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Docs
                </Link>
                <Link
                  to="/docs/guides/admins/events"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Guides
                </Link>
                <Link
                  to="/docs/api"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  API Reference
                </Link>
              </nav>
            </div>

            {/* Right: Search and links */}
            <div className="flex items-center gap-4">
              <DocsSearch />

              <Link
                to="/login"
                className="hidden sm:inline-flex text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Get Started
              </Link>

              {/* Mobile menu button */}
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-rack rounded-md"
                aria-label="Open navigation menu"
              >
                <MenuIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600/75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Content wrapper */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex">
          {/* Left sidebar */}
          <aside
            className={`
              fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200
              transform transition-transform duration-300 ease-in-out
              lg:relative lg:translate-x-0 lg:z-0 lg:pt-8 lg:pb-16
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}
          >
            {/* Mobile header */}
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 lg:hidden">
              <span className="text-lg font-semibold text-gray-900">Navigation</span>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-rack rounded-md"
                aria-label="Close navigation menu"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="h-full overflow-y-auto px-4 pt-4 lg:pt-0">
              <DocsSidebar />
            </div>
          </aside>

          {/* Main content */}
          <main
            id="main-content"
            className="flex-1 min-w-0 lg:pl-8 py-8 lg:py-12"
            tabIndex={-1}
          >
            <article className="prose prose-gray max-w-none prose-headings:scroll-mt-20 prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-code:before:content-none prose-code:after:content-none prose-code:bg-slate-100 prose-code:text-slate-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-normal">
              <MDXProvider>
                <Outlet />
              </MDXProvider>
            </article>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-16">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} ProgramRow. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-gray-700">
                Home
              </Link>
              <Link to="/docs" className="hover:text-gray-700">
                Documentation
              </Link>
              <Link to="/login" className="hover:text-gray-700">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
